import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { EmployeeAvatar } from "@/components/timeclock/EmployeeAvatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { adminApi, fortnoxApi } from "@/lib/api";
import { fileToAvatarBlob } from "@/lib/image";
import { ATTENDANCE_CAUSE_CODES, ATTENDANCE_CAUSE_LABELS } from "#shared/fortnox";
import type { Employee } from "#shared/types";

type Draft = {
  id?: string;
  name: string;
  employee_number: string;
  active: boolean;
  sort_order: number;
  vacation_days_per_year: number;
  attendance_code: string;
  /** Ny bild som väntar på att sparas, och en lokal förhandsvisning av den. */
  photo?: Blob | null;
  photo_preview?: string | null;
  existing_photo_url?: string | null;
};

const emptyDraft: Draft = {
  name: "",
  employee_number: "",
  active: true,
  sort_order: 0,
  vacation_days_per_year: 25,
  attendance_code: "TID",
};

export function EmployeesPanel() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: adminApi.employees,
  });

  // Förslagen kräver en Fortnox-koppling. Utan den får man skriva numret själv,
  // så ett fel här ska inte störa formuläret.
  const { data: fortnoxEmployees = [] } = useQuery({
    queryKey: ["fortnox", "employees"],
    queryFn: fortnoxApi.employees,
    enabled: !!draft,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const typedNumber = draft?.employee_number.trim() ?? "";
  const matchedInFortnox = typedNumber
    ? fortnoxEmployees.find((person) => person.employee_id === typedNumber)
    : undefined;

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["employees"] });
  }

  const save = useMutation({
    mutationFn: async (value: Draft) => {
      const payload = {
        name: value.name.trim(),
        employee_number: value.employee_number.trim() || null,
        active: value.active,
        sort_order: value.sort_order,
        vacation_days_per_year: value.vacation_days_per_year,
        attendance_code: value.attendance_code,
      };

      const employee = value.id
        ? await adminApi.updateEmployee(value.id, payload)
        : await adminApi.createEmployee(payload);

      if (value.photo) await adminApi.uploadPhoto(employee.id, value.photo);
      else if (value.photo === null && value.existing_photo_url) {
        await adminApi.deletePhoto(employee.id);
      }
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
      toast.success("Sparat");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: adminApi.deleteEmployee,
    onSuccess: () => {
      invalidate();
      toast.success("Den anställda är borttagen");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  async function handlePhoto(file: File) {
    try {
      const blob = await fileToAvatarBlob(file);
      setDraft((current) =>
        current ? { ...current, photo: blob, photo_preview: URL.createObjectURL(blob) } : current,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Kunde inte läsa bilden");
    }
  }

  const previewUrl =
    draft?.photo_preview ?? (draft?.photo === null ? null : draft?.existing_photo_url);

  return (
    <div className="surface p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Personal</h2>
          <p className="text-sm text-muted-foreground">
            Anställningsnumret är det som Fortnox matchar lönerader mot.
          </p>
        </div>
        <Button
          className="rounded-full"
          onClick={() => setDraft({ ...emptyDraft, sort_order: employees.length })}
        >
          <Plus className="size-4" /> Lägg till anställd
        </Button>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-muted-foreground">Hämtar…</p>
      ) : employees.length === 0 ? (
        <p className="py-10 text-center text-muted-foreground">Ingen personal upplagd ännu.</p>
      ) : (
        <ul className="mt-5 divide-y">
          {employees.map((employee: Employee) => (
            <li key={employee.id} className="flex items-center gap-4 py-3">
              <EmployeeAvatar
                name={employee.name}
                photoUrl={employee.photo_url}
                className="size-12 text-3xl"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {employee.name}
                  {!employee.active && (
                    <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      Dold
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {employee.employee_number
                    ? `Nr ${employee.employee_number}`
                    : "Inget anställningsnummer"}{" "}
                  · {employee.vacation_days_per_year} semesterdagar/år · {employee.attendance_code}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                aria-label={`Redigera ${employee.name}`}
                onClick={() =>
                  setDraft({
                    id: employee.id,
                    name: employee.name,
                    employee_number: employee.employee_number ?? "",
                    active: employee.active,
                    sort_order: employee.sort_order,
                    vacation_days_per_year: employee.vacation_days_per_year,
                    attendance_code: employee.attendance_code,
                    existing_photo_url: employee.photo_url,
                  })
                }
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full text-destructive"
                aria-label={`Ta bort ${employee.name}`}
                onClick={() => {
                  if (
                    confirm(`Ta bort ${employee.name} och alla registrerade tider för personen?`)
                  ) {
                    remove.mutate(employee.id);
                  }
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!draft} onOpenChange={(open) => !open && setDraft(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Redigera anställd" : "Ny anställd"}</DialogTitle>
          </DialogHeader>

          {draft && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <EmployeeAvatar
                  name={draft.name || "?"}
                  photoUrl={previewUrl}
                  className="size-20 text-5xl"
                />
                <div className="space-y-2">
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void handlePhoto(file);
                      event.target.value = "";
                    }}
                  />
                  <Button
                    variant="secondary"
                    className="rounded-full"
                    onClick={() => fileInput.current?.click()}
                  >
                    <Upload className="size-4" /> Välj bild
                  </Button>
                  {previewUrl && (
                    <Button
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => setDraft({ ...draft, photo: null, photo_preview: null })}
                    >
                      Ta bort bild
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="employee-name">Namn</Label>
                <Input
                  id="employee-name"
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  placeholder="Anna Karlsson"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="employee-number">Anställningsnr i Fortnox</Label>
                  <Input
                    id="employee-number"
                    list="fortnox-employees"
                    value={draft.employee_number}
                    onChange={(event) =>
                      setDraft({ ...draft, employee_number: event.target.value })
                    }
                    placeholder="Lämna tomt tills personen finns i Fortnox"
                  />
                  <datalist id="fortnox-employees">
                    {fortnoxEmployees.map((person) => (
                      <option
                        key={person.employee_id}
                        value={person.employee_id}
                        label={`${person.name}${person.inactive ? " (avslutad)" : ""}`}
                      />
                    ))}
                  </datalist>
                  {typedNumber ? (
                    <p
                      className={
                        matchedInFortnox
                          ? "text-xs text-muted-foreground"
                          : "text-xs text-destructive"
                      }
                    >
                      {matchedInFortnox
                        ? `Fortnox: ${matchedInFortnox.name}`
                        : fortnoxEmployees.length > 0
                          ? "Numret finns inte i Fortnox."
                          : "Kan inte kontrolleras — Fortnox är inte anslutet."}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Utan nummer hoppas personen över vid export.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vacation-days">Semesterdagar per år</Label>
                  <Input
                    id="vacation-days"
                    type="number"
                    min={0}
                    max={365}
                    value={draft.vacation_days_per_year}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        vacation_days_per_year: Number(event.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="attendance-code">Närvarokod i Fortnox</Label>
                <select
                  id="attendance-code"
                  value={draft.attendance_code}
                  onChange={(event) => setDraft({ ...draft, attendance_code: event.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-xs"
                >
                  {ATTENDANCE_CAUSE_CODES.map((code) => (
                    <option key={code} value={code}>
                      {code} – {ATTENDANCE_CAUSE_LABELS[code]}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Koden måste följa anställningsformen. <strong>TID</strong> för månadslön,{" "}
                  <strong>ARB</strong> för timlön — <strong>ARB</strong> på någon med månadslön kan
                  ge timlön ovanpå lönen. Fortnox räknar själv ut flexsaldot mot personens schema.
                </p>
              </div>

              <div className="flex items-center justify-between rounded-2xl bg-muted px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Visa i panelen</p>
                  <p className="text-xs text-muted-foreground">
                    Stäng av för personer som slutat — tidigare tider finns kvar.
                  </p>
                </div>
                <Switch
                  checked={draft.active}
                  onCheckedChange={(checked) => setDraft({ ...draft, active: checked })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setDraft(null)}>
              Avbryt
            </Button>
            <Button
              className="rounded-full"
              disabled={!draft?.name.trim() || save.isPending}
              onClick={() => draft && save.mutate(draft)}
            >
              {save.isPending ? "Sparar…" : "Spara"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
